import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Parse from 'parse';

const EditProfile = () => {
  const [profile, setProfile] = useState({ email: '', phone: '' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [originalFirstName, setOriginalFirstName] = useState('');
  const [originalLastName, setOriginalLastName] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        if (!sessionToken) throw new Error('No session token found. Please login again.');
        await Parse.User.become(sessionToken);
        const response = await Parse.Cloud.run('getOperatorProfile');
        if (response.success && response.operator) {
          const name = response.operator.name || '';
          const spaceIdx = name.indexOf(' ');
          const fn = spaceIdx >= 0 ? name.slice(0, spaceIdx) : name;
          const ln = spaceIdx >= 0 ? name.slice(spaceIdx + 1) : '';
          setFirstName(fn);
          setLastName(ln);
          setOriginalFirstName(fn);
          setOriginalLastName(ln);
          setProfile({
            email: response.operator.email || '',
            phone: response.operator.phone || ''
          });
        }
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || 'Failed to load profile',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.firstName = 'First Name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      await Parse.User.become(sessionToken);
      const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
      const response = await Parse.Cloud.run('updateOperatorProfile', { name: fullName });
      if (response.success) {
        localStorage.setItem('operatorName', response.operator.name || fullName);
        const name = response.operator.name || fullName;
        const spaceIdx = name.indexOf(' ');
        const fn = spaceIdx >= 0 ? name.slice(0, spaceIdx) : name;
        const ln = spaceIdx >= 0 ? name.slice(spaceIdx + 1) : '';
        setFirstName(fn);
        setLastName(ln);
        setOriginalFirstName(fn);
        setOriginalLastName(ln);
        setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update profile',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFirstName(originalFirstName);
    setLastName(originalLastName);
    setErrors({});
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#FFC107' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, px: 2, maxWidth: 600 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Card
        sx={{
          backgroundColor: '#1E1E1E',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ color: '#FFF', fontSize: 18, fontWeight: 600, mb: 1 }}>
            Edit Profile
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', mb: 3 }}>
            Update your operator profile information
          </Typography>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 3 }} />

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Name */}
              <Box>
                <Typography sx={labelSx}>First Name *</Typography>
                <TextField
                  fullWidth
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) setErrors((p) => ({ ...p, firstName: '' }));
                  }}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  placeholder="Enter first name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={editFieldSx}
                />
              </Box>

              {/* Last Name */}
              <Box>
                <Typography sx={labelSx}>Last Name</Typography>
                <TextField
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={editFieldSx}
                />
              </Box>

              {/* Email - Read Only */}
              <Box>
                <Typography sx={labelSx}>Email Address</Typography>
                <TextField
                  fullWidth
                  value={profile.email}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={readonlyFieldSx}
                />
              </Box>

              {/* Phone - Read Only */}
              <Box>
                <Typography sx={labelSx}>Phone Number</Typography>
                <TextField
                  fullWidth
                  value={profile.phone || '-'}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={readonlyFieldSx}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{
                    backgroundColor: '#FFC107',
                    color: '#000',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    '&:hover': { backgroundColor: '#FFD54F' },
                    '&:disabled': {
                      backgroundColor: 'rgba(255,193,7,0.4)',
                      color: 'rgba(0,0,0,0.4)'
                    }
                  }}
                >
                  {saving ? <CircularProgress size={18} sx={{ color: '#000', mr: 1 }} /> : null}
                  {saving ? 'Updating...' : 'Update'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={saving}
                  onClick={handleCancel}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.6)',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.4)',
                      backgroundColor: 'rgba(255,255,255,0.05)'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

const labelSx = { color: 'rgba(255,255,255,0.6)', fontSize: '13px', mb: 1, fontWeight: 500 };

const readonlyFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#141414',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
    '&.Mui-disabled': { backgroundColor: '#141414' }
  },
  '& .MuiOutlinedInput-input': {
    color: 'rgba(255,255,255,0.35)',
    WebkitTextFillColor: 'rgba(255,255,255,0.35)'
  }
};

const editFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2B2B2B',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#FFC107' }
  },
  '& .MuiOutlinedInput-input': { color: '#fff' },
  '& .MuiFormHelperText-root': { color: '#f44336' }
};

export default EditProfile;
